import { Button, Card, ConfirmDialog, useToast } from '@citizen-walk/shared-ui';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { useSessions } from '../sessions-admin';
import { AssignmentSetEditExisting } from './AssignmentSetEditExisting';
import { MembershipPicker } from './MembershipPicker';
import { PostPicker } from './PostPicker';
import { templateDefaultsToPatch } from './templateDefaults';
import { useGenerateAssignments, type GenerateAssignmentsStrategy } from './useGenerateAssignments';
import { useVerificationTemplates } from './useVerificationTemplates';

/**
 * VerificationAssignmentEditPage (Sprint BG-DataSettings 10、Phase 5、GAP A-S22 + F16)。
 *
 * 旧 `old/apps/admin/src/pages/VerificationAssignmentEditorPage.tsx` を v2 に踏襲しつつ:
 *   - 旧の client-side 配分計算 + setDoc を、callable `generateAssignments` 1 発に置換
 *   - 投稿選択は `PostPicker` (search + 全選択)
 *   - 配分先 user は `MembershipPicker` (検索 + 全選択)
 *   - 戦略: random_per_user / round_robin / all_users
 *   - constraints: excludeOwnPosts + maxPerUser
 *   - REQ-4.3.20 (2026-09-04): 対象セッション (sessionIds) を選べるようにし、PostPicker /
 *     MembershipPicker をそのセッションで絞り込む。あわせて「各参加者にはその人の
 *     参加セッションの投稿だけを配る」(constraints.sameSessionOnly) を追加。いずれも旧2
 *     `generateVerificationAssignments` の session 配分を v2 の UI 駆動 callable に移したもの。
 *   - 既存 set 編集 (`:setId` 指定) は AW4 (docs/108 Critical #13) で別 path に分離:
 *     `AssignmentSetEditExisting` が set を load → prefill → merge update する
 *     (callable を再呼び出しせず、新規 set を増やさない)。新規作成 (`/new`) のみ本 form の
 *     generateAssignments callable フローを使う。
 *
 * AW4 (docs/108) で復活した編集フィールド (edit-existing path 側):
 *   - #12 status (下書き/公開中/クローズ) 切替 + 公開中以外の赤警告
 *   - #4 startAt / endAt
 *   - #5 sessionIds 複数選択
 *   - #6 random_per_user 制約 (preferOwnPosts / minOwnPosts) + 生成済明細表
 *
 * 認可: master / district admin (担当地区) のみ。callable / firestore.rules 側でも guard。
 */

const STRATEGY_OPTIONS: {
  value: GenerateAssignmentsStrategy;
  label: string;
  help: string;
}[] = [
  {
    value: 'random_per_user',
    label: 'ランダム配分 (random_per_user)',
    help: '各参加者に、対象投稿の中から「1 人あたりの割当上限」までをランダムに割り当てます。',
  },
  {
    value: 'round_robin',
    label: '均等配分 (round_robin)',
    help: '対象投稿を参加者へ順番に 1 件ずつ割り当てます (上限の指定が可能)。',
  },
  {
    value: 'all_users',
    label: '全員に全投稿 (all_users)',
    help: '対象投稿すべてを全参加者に割り当てます (投稿数 × 参加者数の上限は 10000 件)。',
  },
];

export function VerificationAssignmentEditPage() {
  const { districtId, setId } = useParams<{ districtId: string; setId?: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const templatesState = useVerificationTemplates(districtId ?? null);
  const sessionsState = useSessions(districtId ?? '');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { state: genState, call: callGenerate, reset } = useGenerateAssignments();

  const [templateId, setTemplateId] = useState<string>('');
  const [sessionIds, setSessionIds] = useState<string[]>([]);
  // REQ-4.3.20: 各参加者に「自分が参加したセッションの投稿」だけを配る (全戦略で有効)。
  const [sameSessionOnly, setSameSessionOnly] = useState(false);
  const [postIds, setPostIds] = useState<string[]>([]);
  const [userUids, setUserUids] = useState<string[]>([]);
  const [strategy, setStrategy] = useState<GenerateAssignmentsStrategy>('random_per_user');
  const [excludeOwnPosts, setExcludeOwnPosts] = useState(true);
  const [maxPerUserText, setMaxPerUserText] = useState('5');
  // R-70 P4 (REQ-4.3.53): 自分の投稿のセルフ検証。random_per_user かつ「除外しない」ときのみ有効。
  const [minOwnPostsText, setMinOwnPostsText] = useState('0');
  const [preferOwnPosts, setPreferOwnPosts] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  // docs/98 R-82: テンプレートの配分デフォルトを反映したときの補足 (自由検証向け等)
  const [templateNotes, setTemplateNotes] = useState<string[]>([]);

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canManage = isMaster || isDistrictAdminOfThis;

  const templates = templatesState.status === 'ready' ? templatesState.items : [];
  // 対象セッション候補 (アーカイブ済は除外)。未選択 = 地区全体。
  const sessions = useMemo(
    () =>
      sessionsState.status === 'ready' ? sessionsState.items.filter((s) => !s.archivedAt) : [],
    [sessionsState],
  );
  const sessionNameById = useMemo(
    () => Object.fromEntries(sessions.map((s) => [s.id, s.name])) as Record<string, string>,
    [sessions],
  );
  const maxPerUser = maxPerUserText === '' ? undefined : Number(maxPerUserText);
  // R-70 P4: 自分の投稿のセルフ検証設定が有効になる条件 (ランダム配分 + 自分の投稿を除外しない)。
  const ownInclusionActive = strategy === 'random_per_user' && !excludeOwnPosts;
  const minOwnPosts = minOwnPostsText === '' ? 0 : Number(minOwnPostsText);

  const validationError = useMemo<string | null>(() => {
    if (!templateId) return 'テンプレートを選択してください';
    if (postIds.length === 0) return '投稿を 1 件以上選択してください';
    if (userUids.length === 0) return '配分先 user を 1 名以上選択してください';
    if (maxPerUser !== undefined && (!Number.isFinite(maxPerUser) || maxPerUser <= 0)) {
      return '「1 人あたりの割当上限」は 1 以上の整数で入力してください';
    }
    if (ownInclusionActive && (!Number.isInteger(minOwnPosts) || minOwnPosts < 0)) {
      return '自分の投稿の最低件数は 0 以上の整数で指定してください';
    }
    if (ownInclusionActive && maxPerUser !== undefined && minOwnPosts > maxPerUser) {
      return '自分の投稿の最低件数は「1 人あたり上限」以下にしてください';
    }
    if (strategy === 'all_users' && postIds.length * userUids.length > 10000) {
      return `all_users では post × user が 10000 件以下である必要があります (現在 ${postIds.length * userUids.length})`;
    }
    return null;
  }, [
    templateId,
    postIds.length,
    userUids.length,
    maxPerUser,
    strategy,
    ownInclusionActive,
    minOwnPosts,
  ]);

  const estimatedCount = useMemo(() => {
    if (validationError) return 0;
    if (strategy === 'all_users') return postIds.length * userUids.length;
    if (strategy === 'random_per_user') {
      const max = maxPerUser ?? postIds.length;
      return Math.min(postIds.length, max) * userUids.length;
    }
    // round_robin: 最大 postIds.length 件、maxPerUser*userUids.length が上限
    const cap = maxPerUser !== undefined ? maxPerUser * userUids.length : Infinity;
    return Math.min(postIds.length, cap);
  }, [validationError, strategy, postIds.length, userUids.length, maxPerUser]);

  /**
   * テンプレート選択時: テンプレートの「配分デフォルト」を初期値として反映する (docs/98 R-82)。
   * テンプレート編集画面の案内「テンプレを選んだ時点で自動入力されます」どおり、選び直すたびに上書きする
   * (地区側は入力後に自由に変えられる)。
   */
  function handleTemplateChange(nextId: string) {
    setTemplateId(nextId);
    const patch = templateDefaultsToPatch(templates.find((t) => t.id === nextId));
    setTemplateNotes(patch.notes);
    if (!patch.hasAny) return;
    if (patch.strategy) setStrategy(patch.strategy);
    if (patch.maxPerUser !== undefined) setMaxPerUserText(String(patch.maxPerUser));
    if (patch.excludeOwnPosts !== undefined) setExcludeOwnPosts(patch.excludeOwnPosts);
    if (patch.minOwnPosts !== undefined) setMinOwnPostsText(String(patch.minOwnPosts));
    if (patch.preferOwnPosts !== undefined) setPreferOwnPosts(patch.preferOwnPosts);
    if (patch.sameSessionOnly !== undefined) setSameSessionOnly(patch.sameSessionOnly);
    toast('info', 'テンプレートの推奨設定を反映しました（必要に応じて変更できます）');
  }

  function handleSubmit() {
    if (validationError) {
      toast('error', validationError);
      return;
    }
    setConfirmOpen(true);
  }

  async function handleConfirm() {
    setConfirmOpen(false);
    if (!districtId) return;
    const res = await callGenerate({
      districtId,
      templateId,
      strategy,
      postIds,
      userUids,
      constraints: {
        excludeOwnPosts,
        ...(maxPerUser !== undefined ? { maxPerUser } : {}),
        // R-70 P4: セルフ検証 (自分の投稿を含める) は random_per_user かつ除外しないときのみ送る。
        ...(ownInclusionActive && minOwnPosts > 0 ? { minOwnPosts } : {}),
        ...(ownInclusionActive && preferOwnPosts ? { preferOwnPosts: true } : {}),
        // REQ-4.3.20: 参加者ごとに「その人の参加セッションの投稿」だけを配る。
        ...(sameSessionOnly ? { sameSessionOnly: true } : {}),
      },
      ...(sessionIds.length > 0 ? { sessionIds } : {}),
      ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
      ...(description ? { description } : {}),
    });
    if (res) {
      toast('success', `配分セットを作成しました (${res.assignmentCount} 件)`);
      // 生成済セットに遷移 (本 sprint では新規 form を再利用するため一覧に戻る方が自然)
      navigate(`/districts/${districtId}/verification/assignmentSets`);
    } else {
      toast('error', `配分失敗: ${genState.error?.message ?? 'unknown'}`);
    }
  }

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}/verification/assignmentSets` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 配分セット一覧
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          {setId ? '配分セット編集' : '配分セット新規作成'}
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {setId
            ? 'REQ-4.3.20〜25 / docs/108 AW4 — 既存セットを読み込み、公開状態・期間・対象セッション・配分制約を編集して保存します。'
            : 'REQ-4.3.20〜25 / GAP A-S22 — テンプレ + PostPicker + MembershipPicker + 戦略 + constraints を指定し、generateAssignments callable で AssignmentSet を新規作成します (rate limit: 1/min)。'}
        </p>
      </header>

      {/* docs/108 Critical #13 — 既存セット編集は load + merge update の専用 path */}
      {setId && districtId && (
        <AssignmentSetEditExisting
          districtId={districtId}
          setId={setId}
          canManage={canManage}
          actorUid={authState.user?.uid ?? ''}
        />
      )}

      {!setId && !canManage && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区の配分セット管理権限がありません (role: {authState.role ?? 'none'})。
          </p>
        </Card>
      )}

      {!setId && (
        <>
          {/* 2026-09-04 — セッション終了後にまとめて配る用途は「配分の予約」へ誘導 */}
          <Card padding="md" className="mb-4 border-primary-200 bg-primary-50/40">
            <p className="text-xs text-slate-700">
              この画面は、いまある投稿を選んでその場で配る方法です。セッション終了後にまとめて配りたい場合は、投稿を選ばずに配り方だけを保存できる
              <Link
                to={districtId ? `/districts/${districtId}/verification/plans/new` : '/districts'}
                className="mx-1 text-primary-700 underline"
              >
                配分の予約
              </Link>
              をお使いください。
            </p>
          </Card>

          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-3">1. テンプレート</h2>
            {templatesState.status === 'loading' && (
              <p className="text-sm text-slate-600">読込中…</p>
            )}
            {templatesState.status === 'error' && (
              <p className="text-sm text-danger-700">読込エラー: {templatesState.error.message}</p>
            )}
            {templatesState.status === 'ready' && templates.length === 0 && (
              <p className="text-sm text-warning-700">
                テンプレートがありません。先に検証テンプレを作成してください。
              </p>
            )}
            {templatesState.status === 'ready' && templates.length > 0 && (
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">テンプレート *</span>
                <select
                  value={templateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  disabled={!canManage}
                  aria-label="templateId"
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
                >
                  <option value="">— 選択してください —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      [{t.status}] {t.displayName || '(無題)'} (v{t.version})
                    </option>
                  ))}
                </select>
              </label>
            )}
            {templateNotes.map((n) => (
              <p key={n} className="mt-2 text-xs text-primary-800">
                {n}
              </p>
            ))}
          </Card>

          {/* REQ-4.3.20 — 対象セッション。未選択は地区全体 (旧2 の sessionIds 踏襲)。 */}
          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-3">2. 対象セッション</h2>
            <p className="text-xs text-slate-500 mb-2">
              検証の対象にするセッションを選びます。選ぶと、下の投稿一覧と参加者一覧がそのセッションの分だけに絞られ、
              作成した配分はそのセッションを参加セッションに持つ人にだけ表示されます。選ばなければ地区全体が対象です。
            </p>
            {sessionsState.status === 'loading' && (
              <p className="text-sm text-slate-600">読込中…</p>
            )}
            {sessionsState.status === 'error' && (
              <p className="text-sm text-danger-700">読込エラー: {sessionsState.error.message}</p>
            )}
            {sessionsState.status === 'ready' && sessions.length === 0 && (
              <p className="text-sm text-slate-600">
                この地区にはセッションが登録されていません (地区全体が対象になります)。
              </p>
            )}
            {sessions.length > 0 && (
              <div className="grid grid-cols-1 gap-1">
                {sessions.map((s) => {
                  const checked = sessionIds.includes(s.id);
                  return (
                    <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSessionIds((prev) =>
                            prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id],
                          )
                        }
                        disabled={!canManage}
                        aria-label={`対象セッション ${s.name}`}
                      />
                      <span className="min-w-0">
                        {s.name}
                        <span className="ml-1 text-[11px] text-slate-500">
                          投稿 {s.postCount} 件
                        </span>
                      </span>
                    </label>
                  );
                })}
                <p className="text-[11px] text-slate-500 mt-1">
                  {sessionIds.length === 0
                    ? '未選択 — 地区全体が対象です。'
                    : `${sessionIds.length} 件のセッションを選択中。`}
                </p>
              </div>
            )}
          </Card>

          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-3">3. 対象投稿 (PostPicker)</h2>
            {districtId && (
              <PostPicker
                districtId={districtId}
                value={postIds}
                onChange={setPostIds}
                disabled={!canManage}
                sessionIds={sessionIds}
                sessionNameById={sessionNameById}
              />
            )}
          </Card>

          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-3">
              4. 配分先 user (MembershipPicker)
            </h2>
            {districtId && (
              <MembershipPicker
                districtId={districtId}
                value={userUids}
                onChange={setUserUids}
                disabled={!canManage}
                sessionIds={sessionIds}
                sessionNameById={sessionNameById}
              />
            )}
          </Card>

          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-3">5. 配分戦略 (strategy)</h2>
            <div className="grid grid-cols-1 gap-2">
              {STRATEGY_OPTIONS.map((o) => (
                <label key={o.value} className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="strategy"
                    value={o.value}
                    checked={strategy === o.value}
                    onChange={() => setStrategy(o.value)}
                    disabled={!canManage}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium">{o.label}</span>
                    <span className="block text-[11px] text-slate-500">{o.help}</span>
                  </span>
                </label>
              ))}
            </div>
          </Card>

          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-3">6. 制約 (constraints)</h2>
            <div className="grid grid-cols-1 gap-3">
              {/* REQ-4.3.20 — 参加者ごとに「その人の参加セッションの投稿」だけを配る (旧2 踏襲)。 */}
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={sameSessionOnly}
                  onChange={(e) => setSameSessionOnly(e.target.checked)}
                  disabled={!canManage}
                  className="mt-1"
                  aria-label="参加セッションの投稿だけを割り当てる"
                />
                <span>
                  <span className="font-medium">
                    各参加者には、その人の参加セッションの投稿だけを割り当てる
                  </span>
                  <span className="block text-[11px] text-slate-500">
                    自分が歩いた場所の投稿だけを確認してもらう設定です。参加セッションは参加者ごとに判定し、
                    端末を引き継いだ場合は引き継ぎ前の参加分も対象になります。
                    どのセッションにも紐づいていない投稿は、この設定でも全員に割り当てられます。
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={excludeOwnPosts}
                  onChange={(e) => setExcludeOwnPosts(e.target.checked)}
                  disabled={!canManage}
                  className="mt-1"
                  aria-label="自分の投稿を除外する"
                />
                <span>
                  <span className="font-medium">自分の投稿を配分から除外する</span>
                  <span className="block text-[11px] text-slate-500">
                    各参加者には他の人の投稿だけを割り当てます (第三者の目で確認してもらう用途)。
                    オフにすると、下の「自分の投稿のセルフ検証」を設定できます。
                  </span>
                </span>
              </label>
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">
                  1 人あたりの割当上限 (空欄=無制限、「全員に全投稿」では無視)
                </span>
                <input
                  type="number"
                  value={maxPerUserText}
                  onChange={(e) => setMaxPerUserText(e.target.value)}
                  disabled={!canManage || strategy === 'all_users'}
                  min={1}
                  aria-label="1人あたりの割当上限"
                  className="w-32 min-h-touch px-3 border border-slate-300 rounded text-sm"
                />
              </label>

              {/* R-70 P4 (REQ-4.3.53): 自分の投稿のセルフ検証。ランダム配分かつ除外しないときのみ。 */}
              {ownInclusionActive && (
                <div className="border border-primary-200 bg-primary-50/40 rounded p-3 grid grid-cols-1 gap-3">
                  <p className="text-xs text-slate-700">
                    <span className="font-medium text-primary-900">自分の投稿のセルフ検証</span>
                    <span className="block mt-0.5">
                      参加者本人に「自分が撮った投稿の AI 解析結果」を確認してもらう設定です。
                      端末を引き継いだ場合も、引き継ぎ前の端末で撮った投稿を「自分の投稿」として扱います。
                    </span>
                  </p>
                  <label className="grid grid-cols-1 gap-1">
                    <span className="text-xs text-slate-600">
                      自分の投稿を最低何件含めるか (0 = 指定なし)
                    </span>
                    <input
                      type="number"
                      value={minOwnPostsText}
                      onChange={(e) => setMinOwnPostsText(e.target.value)}
                      disabled={!canManage}
                      min={0}
                      step={1}
                      aria-label="自分の投稿を最低何件含めるか"
                      className="w-32 min-h-touch px-3 border border-slate-300 rounded text-sm"
                    />
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={preferOwnPosts}
                      onChange={(e) => setPreferOwnPosts(e.target.checked)}
                      disabled={!canManage}
                      className="mt-1"
                      aria-label="空き枠も自分の投稿で優先的に埋める"
                    />
                    <span>
                      <span className="font-medium">空き枠も自分の投稿で優先的に埋める</span>
                      <span className="block text-[11px] text-slate-500">
                        オン: 上限に達するまで自分の投稿を優先して割り当てます。 オフ:
                        自分の投稿は上の最低件数までに抑え、残りは他の人の投稿で埋めます。
                      </span>
                    </span>
                  </label>
                </div>
              )}
            </div>
          </Card>

          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-3">
              7. AssignmentSet メタ (任意)
            </h2>
            <div className="grid grid-cols-1 gap-3">
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">displayName (省略時は自動生成)</span>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={!canManage}
                  maxLength={120}
                  aria-label="displayName"
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                />
              </label>
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">description</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!canManage}
                  rows={2}
                  aria-label="description"
                  className="px-3 py-2 border border-slate-300 rounded text-sm"
                />
              </label>
            </div>
          </Card>

          {validationError && (
            <Card padding="md" className="border-warning-600 mb-4">
              <p className="text-sm text-warning-700">⚠ {validationError}</p>
            </Card>
          )}

          <div className="flex items-center justify-between gap-2 mb-6 flex-wrap">
            <p className="text-sm text-slate-600">
              配分予定件数 (見積): <span className="font-bold">{estimatedCount}</span> 件
              {sameSessionOnly && (
                <span className="block text-[11px] text-slate-500">
                  ※ 参加セッションで絞るため、実際の件数はこれより少なくなります。
                </span>
              )}
            </p>
            <Button
              onClick={handleSubmit}
              disabled={!canManage || !!validationError || genState.status === 'pending'}
            >
              {genState.status === 'pending' ? '生成中…' : '🎯 配分セットを生成'}
            </Button>
          </div>

          {genState.status === 'success' && genState.result && (
            <Card padding="lg" className="border-success-600 mb-4">
              <h3 className="text-sm font-bold text-success-800 mb-2">
                ✓ 生成完了 — assignmentSetId:{' '}
                <code className="text-xs">{genState.result.assignmentSetId}</code>
              </h3>
              <ul className="text-xs text-slate-700 grid grid-cols-1 gap-1">
                <li>戦略: {genState.result.strategy}</li>
                <li>合計 assignment 件数: {genState.result.assignmentCount}</li>
              </ul>
              <button
                type="button"
                onClick={reset}
                className="mt-3 text-xs text-primary-700 hover:underline"
              >
                続けて新規生成
              </button>
            </Card>
          )}

          {genState.status === 'error' && (
            <Card padding="md" className="border-danger-600 mb-4">
              <p className="text-sm text-danger-700">配分失敗: {genState.error.message}</p>
            </Card>
          )}

          <ConfirmDialog
            open={confirmOpen}
            title="配分セットを生成しますか?"
            message={
              `新規 AssignmentSet を作成し、${estimatedCount} 件の assignment を生成します。\n` +
              `戦略: ${strategy}\npost: ${postIds.length} 件 / user: ${userUids.length} 名\n` +
              `対象セッション: ${
                sessionIds.length === 0
                  ? '地区全体'
                  : sessionIds.map((sid) => sessionNameById[sid] ?? sid).join(' / ')
              }` +
              (sameSessionOnly
                ? '\n各参加者には、その人の参加セッションの投稿だけを割り当てます。'
                : '')
            }
            confirmLabel="実行"
            cancelLabel="キャンセル"
            intent="warning"
            onConfirm={() => void handleConfirm()}
            onCancel={() => setConfirmOpen(false)}
          />
        </>
      )}
    </>
  );
}
